/**
 * Intercepteur Axios global pour gérer le refresh token automatiquement
 * S'applique à toutes les instances d'Axios dans l'application
 */
import axios from 'axios';
import store from '@/store';
import router from '@/router';

// Variables pour gérer le refresh token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Rafraîchit le token d'authentification
 */
const refreshToken = async () => {
  const refreshTokenValue = store.state.userAuthentified?.refreshToken;
  const currentToken = store.state.userAuthentified?.token;

  if (!refreshTokenValue) {
    throw new Error('Aucun refresh token disponible');
  }

  const dataToRefreshToSend = {
    data: {
      refreshToken: refreshTokenValue,
      token: currentToken,
    },
  };

  try {
    const response = await axios.post('refreshToken', dataToRefreshToSend);
    
    if (response.data?.status?.code === 800 && response.data?.item?.token) {
      // Mettre à jour le token dans Vuex
      store.commit('updateToken', response.data.item);
      return response.data.item.token;
    } else {
      throw new Error('Échec du rafraîchissement du token');
    }
  } catch (error) {
    console.error('❌ Erreur lors du refresh token:', error);
    throw error;
  }
};

/**
 * Configure les intercepteurs Axios globaux
 */
export const setupAxiosInterceptors = () => {
  // Intercepteur de requête : ajouter le token automatiquement
  axios.interceptors.request.use(
    (config) => {
      const token = store.state.userAuthentified?.token;
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Intercepteur de réponse : gérer les erreurs 401 et refresh token
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      if (error.response) {
        const status = error.response.status;
        const url = error.config?.url || 'URL inconnue';
        
        // Gestion du 401 (Token expiré)
        if (status === 401) {
          console.warn('🔒 [401] Token expiré pour:', url);
          
          // Ne pas tenter de refresh sur l'endpoint de refresh lui-même
          if (url.includes('refreshToken') || originalRequest._retry) {
            console.error('❌ Impossible de rafraîchir le token, déconnexion nécessaire');
            await store.dispatch('logout');
            router.push('/').catch(() => {});
            return Promise.reject(error);
          }

          // Gestion du refresh token avec file d'attente
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return axios(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const newToken = await refreshToken();
            
            // Mettre à jour le header de la requête originale
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            
            // Traiter la file d'attente
            processQueue(null, newToken);
            
            // Relancer la requête originale
            return axios(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            console.error('❌ Échec du refresh token, déconnexion');
            await store.dispatch('logout');
            router.push('/').catch(() => {});
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        }
        
        // Autres codes d'erreur
        switch (status) {
          case 403:
            console.warn('⛔ [403] Accès interdit pour:', url);
            break;
          case 404:
            console.warn('🔍 [404] Ressource non trouvée:', url);
            break;
          case 500:
            console.error('💥 [500] Erreur serveur pour:', url);
            break;
          default:
            console.error(`❌ [${status}] Erreur API pour:`, url);
        }
      } else if (error.request) {
        console.error('📡 Pas de réponse du serveur');
      } else {
        console.error('⚙️ Erreur de configuration:', error.message);
      }
      
      return Promise.reject(error);
    }
  );
};
